/**
 * Single announcement view page.
 * Displays the full announcement content.
 */

import { Head, Link, usePage } from '@inertiajs/react';
import { EditorRenderer } from '@/components/Editor/EditorRenderer';

interface Announcement {
    id: number;
    title: string;
    /** Markdown content string */
    content: string;
    scope: string;
    created_at: string;
    updated_at: string;
    author: {
        id: number;
        username: string;
    };
}

interface Props {
    announcement: Announcement;
}

export default function Show({ announcement }: Props) {
    const { auth } = usePage().props as { auth: { user: { role: string } } };
    const isGm = auth?.user?.role === 'gm';

    return (
        <>
            <Head title={announcement.title} />
            <div className="min-h-screen bg-slate-900 text-white">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-8">
                        <Link
                            href="/announcements"
                            className="text-slate-300 hover:text-white transition-colors"
                        >
                            ← Back to Announcements
                        </Link>
                    </div>

                    {/* Announcement */}
                    <article className="bg-slate-800 rounded-lg p-8 border border-slate-700">
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <h1 className="text-3xl font-bold text-amber-400 mb-2">
                                    {announcement.title}
                                </h1>
                                <p className="text-slate-300">
                                    Posted by {announcement.author.username} on{' '}
                                    {new Date(announcement.created_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                    })}
                                </p>
                            </div>
                            {isGm && (
                                <Link
                                    href={`/gm/announcements/${announcement.id}/edit`}
                                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                                >
                                    Edit
                                </Link>
                            )}
                        </div>

                        <div className="prose prose-invert prose-lg max-w-none">
                            <EditorRenderer content={announcement.content} />
                        </div>
                    </article>
                </div>
            </div>
        </>
    );
}

